import type {
  ExternalDesignKnowledgeLibraryGapCode,
  LibraryGapCode,
} from '@cogo/agentic-generator'
import type { UxApplicationLibraryAdmissionIssueCode } from '@cogo/kg-browser'
import type { ReadinessReport } from '@/features/generation-readiness/check-readiness'
import type { ReadinessItem } from '@/features/generation-readiness/translate-gaps'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'

type NonKoreanLocale = Exclude<LocaleCode, 'ko'>
type KnownMachineCode =
  | LibraryGapCode
  | ExternalDesignKnowledgeLibraryGapCode
  | UxApplicationLibraryAdmissionIssueCode
type RecoveryKey =
  | 'reloadProject'
  | 'registerSource'
  | 'reviewAuthority'
  | 'chooseVerified'
  | 'waitForValidation'
  | 'repairDefinitions'
  | 'repairConnections'
  | 'repairCapabilities'
  | 'repairJourney'
  | 'reviewContract'
  | 'chooseAvailableCapability'
  | 'reimportSource'
  | 'defineBehavior'

interface ReadinessItemCopy {
  title: string
  explanation: string
}

interface CompactReadinessLocalizationCopy {
  machineCode: Readonly<Record<KnownMachineCode, ReadinessItemCopy>>
  checkFailed: ReadinessItemCopy
  generic: Record<ReadinessItem['kind'], ReadinessItemCopy>
  diagnosticCodeLabel: string
  machineValidationNotPerformed: string
  machineValidationFailed: string
  suggestion: {
    sourceRegistration: string
    designSystem: string
    review: string
  }
}

const MACHINE_CODE_RECOVERY = {
  'library-input-schema-invalid': 'reloadProject',
  'library-source-forbidden': 'registerSource',
  'library-lineage-missing': 'reviewAuthority',
  'ux-pattern-not-admitted': 'chooseVerified',
  'application-blueprint-not-admitted': 'waitForValidation',
  'duplicate-ux-pattern-ref': 'repairDefinitions',
  'duplicate-ux-instance-id': 'repairDefinitions',
  'duplicate-library-port-id': 'repairConnections',
  'unbound-pattern-port': 'repairConnections',
  'incompatible-pattern-port': 'repairConnections',
  'cross-scope-local-reference': 'repairConnections',
  'ux-pattern-state-invalid': 'repairDefinitions',
  'library-event-invalid': 'repairConnections',
  'library-capability-invalid': 'repairCapabilities',
  'ux-pattern-transition-invalid': 'repairJourney',
  'journey-entry-invalid': 'repairJourney',
  'journey-terminal-missing': 'repairJourney',
  'journey-step-unreachable': 'repairJourney',
  'journey-recovery-invalid': 'repairJourney',
  'exact-namespace-collision': 'repairDefinitions',
  'library-presentation-forbidden': 'reviewContract',
  'application-capability-mismatch': 'repairCapabilities',
  'application-webmcp-capability-unavailable': 'chooseAvailableCapability',
  'external-design-library-input-invalid': 'reimportSource',
  'external-design-selection-rights-invalid': 'registerSource',
  'external-design-business-behavior-gap-missing': 'defineBehavior',
  'external-design-business-behavior-not-observed': 'defineBehavior',
  'external-design-library-bundle-unsafe': 'reimportSource',
  'external-design-authority-ref-contamination': 'reviewAuthority',
  'rdf-input-invalid': 'reloadProject',
  'authority-context-invalid': 'reviewAuthority',
  'ontology-root-missing': 'repairDefinitions',
  'shacl-conformance-failed': 'repairDefinitions',
  'exact-namespace-invalid': 'repairDefinitions',
  'application-envelope-invalid': 'repairDefinitions',
  'definition-reference-invalid': 'repairDefinitions',
  'application-capability-invalid': 'repairCapabilities',
} as const satisfies Record<KnownMachineCode, RecoveryKey>

const RECOVERY_COPY: Record<
  NonKoreanLocale,
  Readonly<Record<RecoveryKey, string>>
> = {
  en: {
    reloadProject: 'Reload the project and run the readiness check again.',
    registerSource:
      'The source does not have verified origin and usage rights. Register them before generating this app.',
    reviewAuthority:
      'Review the source authority and lineage record before generation.',
    chooseVerified:
      'Choose an admitted alternative, or wait until validation is complete.',
    waitForValidation:
      'Wait for validation to complete, then run the readiness check again.',
    repairDefinitions:
      'Repair the referenced definition and validate the project again.',
    repairConnections:
      'Repair the affected reference or connection before generation.',
    repairCapabilities:
      'Align the declared capabilities with the capabilities the app uses.',
    repairJourney:
      'Repair the affected journey path, then check readiness again.',
    reviewContract:
      'Remove the unsupported presentation or publication request before generation.',
    chooseAvailableCapability:
      'Choose an available integration capability or remove this requirement.',
    reimportSource:
      'Import the source again from a verified, readable package.',
    defineBehavior:
      'Define the missing business behavior before using this design for generation.',
  },
  ja: {
    reloadProject: 'プロジェクトを再読み込みし、準備状況をもう一度確認してください。',
    registerSource:
      'ソースの出所と利用権限が確認されていません。このアプリを生成する前に登録してください。',
    reviewAuthority:
      '生成前にソースの権限と系譜記録を確認してください。',
    chooseVerified:
      '検証済みの代替を選ぶか、検証の完了を待ってください。',
    waitForValidation:
      '検証の完了後、準備状況をもう一度確認してください。',
    repairDefinitions:
      '参照先の定義を修正し、プロジェクトを再検証してください。',
    repairConnections:
      '生成前に影響する参照または接続を修正してください。',
    repairCapabilities:
      '宣言した機能をアプリが実際に使う機能と一致させてください。',
    repairJourney:
      '影響するユーザージャーニーを修正し、再確認してください。',
    reviewContract:
      '生成前に未対応の表現または公開要求を取り除いてください。',
    chooseAvailableCapability:
      '利用可能な連携機能を選ぶか、この要件を削除してください。',
    reimportSource:
      '検証済みで読み取り可能なパッケージからソースを再インポートしてください。',
    defineBehavior:
      'このデザインを生成に使う前に、不足している業務動作を定義してください。',
  },
  zh: {
    reloadProject: '请重新加载项目，然后再次检查准备状态。',
    registerSource: '尚未验证此来源的出处和使用权限。请在生成此应用前完成登记。',
    reviewAuthority: '请在生成前检查来源权限和谱系记录。',
    chooseVerified: '请选择已验证的替代项，或等待验证完成。',
    waitForValidation: '请等待验证完成，然后再次检查准备状态。',
    repairDefinitions: '请修复相关定义，然后重新验证项目。',
    repairConnections: '请在生成前修复受影响的引用或连接。',
    repairCapabilities: '请使声明的功能与应用实际使用的功能一致。',
    repairJourney: '请修复受影响的用户旅程路径，然后再次检查。',
    reviewContract: '请在生成前移除不支持的展示或发布请求。',
    chooseAvailableCapability: '请选择可用的集成功能，或移除此要求。',
    reimportSource: '请从已验证且可读的软件包中重新导入来源。',
    defineBehavior: '请先定义缺失的业务行为，再使用此设计进行生成。',
  },
  'zh-TW': {
    reloadProject: '請重新載入專案，然後再次檢查準備狀態。',
    registerSource: '尚未驗證此來源的出處與使用權限。請在產生此應用程式前完成登記。',
    reviewAuthority: '請在產生前檢查來源權限與譜系記錄。',
    chooseVerified: '請選擇已驗證的替代項，或等待驗證完成。',
    waitForValidation: '請等待驗證完成，然後再次檢查準備狀態。',
    repairDefinitions: '請修復相關定義，然後重新驗證專案。',
    repairConnections: '請在產生前修復受影響的參照或連線。',
    repairCapabilities: '請使宣告的功能與應用程式實際使用的功能一致。',
    repairJourney: '請修復受影響的使用者旅程路徑，然後再次檢查。',
    reviewContract: '請在產生前移除不支援的呈現或發佈請求。',
    chooseAvailableCapability: '請選擇可用的整合功能，或移除此要求。',
    reimportSource: '請從已驗證且可讀的軟體包重新匯入來源。',
    defineBehavior: '請先定義缺少的業務行為，再使用此設計進行產生。',
  },
}

const MACHINE_CODE_TITLE: Record<
  NonKoreanLocale,
  Readonly<Record<KnownMachineCode, string>>
> = {
  en: {
    'library-input-schema-invalid': 'Generation input data has an invalid format',
    'library-source-forbidden': 'This source cannot be used for generation',
    'library-lineage-missing': 'Source lineage does not meet generation requirements',
    'ux-pattern-not-admitted': 'The requested UI pattern has not been admitted',
    'application-blueprint-not-admitted': 'The application blueprint has not been admitted',
    'duplicate-ux-pattern-ref': 'A UI pattern reference is duplicated',
    'duplicate-ux-instance-id': 'A UI instance identifier is duplicated',
    'duplicate-library-port-id': 'A library port identifier is duplicated',
    'unbound-pattern-port': 'A required pattern port is not connected',
    'incompatible-pattern-port': 'Two incompatible pattern ports are connected',
    'cross-scope-local-reference': 'A local value is referenced across UI scopes',
    'ux-pattern-state-invalid': 'The UI pattern state definition is invalid',
    'library-event-invalid': 'A UI event reference is invalid',
    'library-capability-invalid': 'A required UI capability is not declared',
    'ux-pattern-transition-invalid': 'A UI state transition is invalid',
    'journey-entry-invalid': 'The user journey entry is invalid',
    'journey-terminal-missing': 'A user journey terminal is missing',
    'journey-step-unreachable': 'A user journey step is unreachable',
    'journey-recovery-invalid': 'A user journey recovery path is invalid',
    'exact-namespace-collision': 'The exact namespace contains a collision',
    'library-presentation-forbidden': 'A presentation or publication contract is violated',
    'application-capability-mismatch': 'Application capability declarations do not match usage',
    'application-webmcp-capability-unavailable': 'The requested WebMCP capability is unavailable',
    'external-design-library-input-invalid': 'Imported design data has an invalid format',
    'external-design-selection-rights-invalid': 'Usage rights for an imported design are unverified',
    'external-design-business-behavior-gap-missing': 'The imported design is missing a behavior-gap declaration',
    'external-design-business-behavior-not-observed': 'Business behavior cannot be observed from the imported design',
    'external-design-library-bundle-unsafe': 'The imported design package cannot be read safely',
    'external-design-authority-ref-contamination': 'An unverified design reference occupies an authority slot',
    'rdf-input-invalid': 'Readiness validation cannot read the RDF input',
    'authority-context-invalid': 'The source authority context is invalid',
    'ontology-root-missing': 'The required ontology root is missing',
    'shacl-conformance-failed': 'The UI structure does not conform to SHACL',
    'exact-namespace-invalid': 'An exact namespace identifier is invalid',
    'application-envelope-invalid': 'The application envelope is invalid',
    'definition-reference-invalid': 'A referenced pattern definition cannot be resolved',
    'application-capability-invalid': 'The application capability list is invalid',
  },
  ja: {
    'library-input-schema-invalid': '生成入力データの形式が無効です',
    'library-source-forbidden': 'このソースは生成に使用できません',
    'library-lineage-missing': 'ソースの系譜が生成要件を満たしていません',
    'ux-pattern-not-admitted': '指定した UI パターンはまだ承認されていません',
    'application-blueprint-not-admitted': 'アプリケーション設計はまだ承認されていません',
    'duplicate-ux-pattern-ref': 'UI パターン参照が重複しています',
    'duplicate-ux-instance-id': 'UI インスタンス ID が重複しています',
    'duplicate-library-port-id': 'ライブラリポート ID が重複しています',
    'unbound-pattern-port': '必須のパターンポートが接続されていません',
    'incompatible-pattern-port': '互換性のないパターンポートが接続されています',
    'cross-scope-local-reference': 'ローカル値が UI スコープを越えて参照されています',
    'ux-pattern-state-invalid': 'UI パターンの状態定義が無効です',
    'library-event-invalid': 'UI イベント参照が無効です',
    'library-capability-invalid': '必要な UI 機能が宣言されていません',
    'ux-pattern-transition-invalid': 'UI 状態遷移が無効です',
    'journey-entry-invalid': 'ユーザージャーニーの開始点が無効です',
    'journey-terminal-missing': 'ユーザージャーニーの終了点が不足しています',
    'journey-step-unreachable': '到達できないユーザージャーニーのステップがあります',
    'journey-recovery-invalid': 'ユーザージャーニーの復旧経路が無効です',
    'exact-namespace-collision': '厳密な名前空間で衝突が発生しています',
    'library-presentation-forbidden': '表現または公開契約に違反しています',
    'application-capability-mismatch': 'アプリの機能宣言と実際の使用が一致しません',
    'application-webmcp-capability-unavailable': '要求された WebMCP 機能は利用できません',
    'external-design-library-input-invalid': 'インポートしたデザインデータの形式が無効です',
    'external-design-selection-rights-invalid': 'インポートしたデザインの利用権が未確認です',
    'external-design-business-behavior-gap-missing': 'インポートしたデザインに動作ギャップの宣言がありません',
    'external-design-business-behavior-not-observed': 'インポートしたデザインから業務動作を確認できません',
    'external-design-library-bundle-unsafe': 'インポートしたデザインパッケージを安全に読めません',
    'external-design-authority-ref-contamination': '未検証のデザイン参照が権限スロットに入っています',
    'rdf-input-invalid': '準備状況の検証で RDF 入力を読み取れません',
    'authority-context-invalid': 'ソース権限コンテキストが無効です',
    'ontology-root-missing': '必要なオントロジールートがありません',
    'shacl-conformance-failed': 'UI 構造が SHACL に適合していません',
    'exact-namespace-invalid': '厳密な名前空間識別子が無効です',
    'application-envelope-invalid': 'アプリケーションエンベロープが無効です',
    'definition-reference-invalid': '参照されたパターン定義を解決できません',
    'application-capability-invalid': 'アプリケーションの機能一覧が無効です',
  },
  zh: {
    'library-input-schema-invalid': '生成输入数据格式无效',
    'library-source-forbidden': '此来源不能用于生成',
    'library-lineage-missing': '来源谱系不符合生成要求',
    'ux-pattern-not-admitted': '请求的 UI 模式尚未通过准入',
    'application-blueprint-not-admitted': '应用蓝图尚未通过准入',
    'duplicate-ux-pattern-ref': 'UI 模式引用重复',
    'duplicate-ux-instance-id': 'UI 实例标识符重复',
    'duplicate-library-port-id': '库端口标识符重复',
    'unbound-pattern-port': '必需的模式端口未连接',
    'incompatible-pattern-port': '连接了不兼容的模式端口',
    'cross-scope-local-reference': '局部值被跨 UI 作用域引用',
    'ux-pattern-state-invalid': 'UI 模式状态定义无效',
    'library-event-invalid': 'UI 事件引用无效',
    'library-capability-invalid': '未声明所需的 UI 功能',
    'ux-pattern-transition-invalid': 'UI 状态转换无效',
    'journey-entry-invalid': '用户旅程入口无效',
    'journey-terminal-missing': '缺少用户旅程终点',
    'journey-step-unreachable': '存在无法到达的用户旅程步骤',
    'journey-recovery-invalid': '用户旅程恢复路径无效',
    'exact-namespace-collision': '精确命名空间存在冲突',
    'library-presentation-forbidden': '违反了展示或发布合同',
    'application-capability-mismatch': '应用功能声明与实际使用不匹配',
    'application-webmcp-capability-unavailable': '请求的 WebMCP 功能不可用',
    'external-design-library-input-invalid': '导入的设计数据格式无效',
    'external-design-selection-rights-invalid': '导入设计的使用权尚未验证',
    'external-design-business-behavior-gap-missing': '导入设计缺少行为空缺声明',
    'external-design-business-behavior-not-observed': '无法从导入设计中观察业务行为',
    'external-design-library-bundle-unsafe': '无法安全读取导入的设计包',
    'external-design-authority-ref-contamination': '未验证的设计引用占用了权威位置',
    'rdf-input-invalid': '准备状态验证无法读取 RDF 输入',
    'authority-context-invalid': '来源权威上下文无效',
    'ontology-root-missing': '缺少所需的本体根节点',
    'shacl-conformance-failed': 'UI 结构不符合 SHACL',
    'exact-namespace-invalid': '精确命名空间标识符无效',
    'application-envelope-invalid': '应用封装无效',
    'definition-reference-invalid': '无法解析引用的模式定义',
    'application-capability-invalid': '应用功能列表无效',
  },
  'zh-TW': {
    'library-input-schema-invalid': '產生輸入資料格式無效',
    'library-source-forbidden': '此來源不能用於產生',
    'library-lineage-missing': '來源譜系不符合產生要求',
    'ux-pattern-not-admitted': '請求的 UI 模式尚未通過准入',
    'application-blueprint-not-admitted': '應用程式藍圖尚未通過准入',
    'duplicate-ux-pattern-ref': 'UI 模式參照重複',
    'duplicate-ux-instance-id': 'UI 實例識別碼重複',
    'duplicate-library-port-id': '程式庫連接埠識別碼重複',
    'unbound-pattern-port': '必要的模式連接埠未連線',
    'incompatible-pattern-port': '連線了不相容的模式連接埠',
    'cross-scope-local-reference': '區域值被跨 UI 範圍參照',
    'ux-pattern-state-invalid': 'UI 模式狀態定義無效',
    'library-event-invalid': 'UI 事件參照無效',
    'library-capability-invalid': '未宣告所需的 UI 功能',
    'ux-pattern-transition-invalid': 'UI 狀態轉換無效',
    'journey-entry-invalid': '使用者旅程入口無效',
    'journey-terminal-missing': '缺少使用者旅程終點',
    'journey-step-unreachable': '存在無法到達的使用者旅程步驟',
    'journey-recovery-invalid': '使用者旅程復原路徑無效',
    'exact-namespace-collision': '精確命名空間存在衝突',
    'library-presentation-forbidden': '違反了呈現或發佈合約',
    'application-capability-mismatch': '應用程式功能宣告與實際使用不符',
    'application-webmcp-capability-unavailable': '請求的 WebMCP 功能無法使用',
    'external-design-library-input-invalid': '匯入的設計資料格式無效',
    'external-design-selection-rights-invalid': '匯入設計的使用權尚未驗證',
    'external-design-business-behavior-gap-missing': '匯入設計缺少行為空缺宣告',
    'external-design-business-behavior-not-observed': '無法從匯入設計中觀察業務行為',
    'external-design-library-bundle-unsafe': '無法安全讀取匯入的設計套件',
    'external-design-authority-ref-contamination': '未驗證的設計參照佔用了權威位置',
    'rdf-input-invalid': '準備狀態驗證無法讀取 RDF 輸入',
    'authority-context-invalid': '來源權威上下文無效',
    'ontology-root-missing': '缺少所需的本體根節點',
    'shacl-conformance-failed': 'UI 結構不符合 SHACL',
    'exact-namespace-invalid': '精確命名空間識別碼無效',
    'application-envelope-invalid': '應用程式封裝無效',
    'definition-reference-invalid': '無法解析參照的模式定義',
    'application-capability-invalid': '應用程式功能清單無效',
  },
}

function buildMachineCodeCopy(
  locale: NonKoreanLocale,
): Readonly<Record<KnownMachineCode, ReadinessItemCopy>> {
  return Object.fromEntries(
    (
      Object.entries(MACHINE_CODE_RECOVERY) as [KnownMachineCode, RecoveryKey][]
    ).map(([machineCode, recoveryKey]) => [
      machineCode,
      {
        title: MACHINE_CODE_TITLE[locale][machineCode],
        explanation: RECOVERY_COPY[locale][recoveryKey],
      },
    ]),
  ) as Record<KnownMachineCode, ReadinessItemCopy>
}

const LOCALIZED_COPY: Record<
  NonKoreanLocale,
  CompactReadinessLocalizationCopy
> = {
  en: {
    machineCode: buildMachineCodeCopy('en'),
    checkFailed: {
      title: 'Generation readiness could not be checked',
      explanation:
        'The readiness check failed, so Studio cannot determine whether generation can proceed. Try again.',
    },
    generic: {
      blocking: {
        title: 'A generation requirement needs attention',
        explanation: 'Resolve this requirement before generation can start.',
      },
      unfilled: {
        title: 'A generation choice is still undecided',
        explanation: 'Decide this item before continuing.',
      },
      advisory: {
        title: 'Review this generation advice',
        explanation: 'Review this recommendation before generation.',
      },
    },
    diagnosticCodeLabel: 'Diagnostic code',
    machineValidationNotPerformed:
      'Machine contract validation runs during generation and is not available before generation.',
    machineValidationFailed:
      'Machine contract validation did not run because the readiness check failed.',
    suggestion: {
      sourceRegistration: 'Open source registration',
      designSystem: 'Open Design System',
      review: 'Review',
    },
  },
  ja: {
    machineCode: buildMachineCodeCopy('ja'),
    checkFailed: {
      title: '生成準備状況を確認できませんでした',
      explanation:
        '準備状況の確認に失敗したため、生成を続行できるか判断できません。再試行してください。',
    },
    generic: {
      blocking: {
        title: '生成要件の確認が必要です',
        explanation: '生成を開始する前に、この要件を解決してください。',
      },
      unfilled: {
        title: '生成に必要な選択が未決定です',
        explanation: '続行する前に、この項目を決定してください。',
      },
      advisory: {
        title: '生成に関する推奨事項を確認してください',
        explanation: '生成前に、この推奨事項を確認してください。',
      },
    },
    diagnosticCodeLabel: '診断コード',
    machineValidationNotPerformed:
      '機械的な契約検証は生成中に実行されるため、生成前にはまだ利用できません。',
    machineValidationFailed:
      '準備状況の確認に失敗したため、機械的な契約検証は実行されませんでした。',
    suggestion: {
      sourceRegistration: 'ソース登録を開く',
      designSystem: 'デザインシステムを開く',
      review: '確認する',
    },
  },
  zh: {
    machineCode: buildMachineCodeCopy('zh'),
    checkFailed: {
      title: '无法检查生成准备状态',
      explanation: '准备状态检查失败，因此 Studio 无法判断能否继续生成。请重试。',
    },
    generic: {
      blocking: {
        title: '有一项生成要求需要处理',
        explanation: '请先解决此要求，再开始生成。',
      },
      unfilled: {
        title: '有一项生成选择尚未决定',
        explanation: '请先决定此项，再继续。',
      },
      advisory: {
        title: '请查看此生成建议',
        explanation: '请在生成前查看此建议。',
      },
    },
    diagnosticCodeLabel: '诊断代码',
    machineValidationNotPerformed:
      '机器合同验证会在生成过程中执行，生成前暂不可用。',
    machineValidationFailed: '准备状态检查失败，因此未执行机器合同验证。',
    suggestion: {
      sourceRegistration: '打开来源登记',
      designSystem: '打开设计系统',
      review: '查看',
    },
  },
  'zh-TW': {
    machineCode: buildMachineCodeCopy('zh-TW'),
    checkFailed: {
      title: '無法檢查產生準備狀態',
      explanation: '準備狀態檢查失敗，因此 Studio 無法判斷是否能繼續產生。請再試一次。',
    },
    generic: {
      blocking: {
        title: '有一項產生要求需要處理',
        explanation: '請先解決此要求，再開始產生。',
      },
      unfilled: {
        title: '有一項產生選擇尚未決定',
        explanation: '請先決定此項，再繼續。',
      },
      advisory: {
        title: '請查看此產生建議',
        explanation: '請在產生前查看此建議。',
      },
    },
    diagnosticCodeLabel: '診斷代碼',
    machineValidationNotPerformed:
      '機器合約驗證會在產生過程中執行，產生前暫不可用。',
    machineValidationFailed: '準備狀態檢查失敗，因此未執行機器合約驗證。',
    suggestion: {
      sourceRegistration: '開啟來源登記',
      designSystem: '開啟設計系統',
      review: '查看',
    },
  },
}

const SAFE_MACHINE_CODE_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)+$/
const SENSITIVE_MACHINE_CODE_PART =
  /(?:^|-)(?:api-key|bearer|credential|password|secret|token)(?:-|$)/

function safeUnknownMachineCode(machineCode: string | undefined): string | undefined {
  if (!machineCode || machineCode.length > 80) return undefined
  if (!SAFE_MACHINE_CODE_PATTERN.test(machineCode)) return undefined
  if (SENSITIVE_MACHINE_CODE_PART.test(machineCode)) return undefined
  return machineCode
}

function isKnownMachineCode(machineCode: string): machineCode is KnownMachineCode {
  return Object.prototype.hasOwnProperty.call(MACHINE_CODE_RECOVERY, machineCode)
}

function localizedSuggestion(
  item: ReadinessItem,
  copy: CompactReadinessLocalizationCopy,
): ReadinessItem['suggestion'] {
  if (!item.suggestion) return undefined
  const label =
    item.suggestion.navigateTo === '/studio/import'
      ? copy.suggestion.sourceRegistration
      : item.suggestion.navigateTo === '/studio/design-system'
        ? copy.suggestion.designSystem
        : copy.suggestion.review
  return { ...item.suggestion, label }
}

/** Localize the compact Chat presentation without changing the shared report. */
export function localizeCompactReadinessItem(
  item: ReadinessItem,
  locale: LocaleCode,
  reportStatus: ReadinessReport['status'],
): ReadinessItem {
  if (locale === 'ko') return item
  const copy = LOCALIZED_COPY[locale] ?? LOCALIZED_COPY.en
  const machineCodeCopy = item.machineCode && isKnownMachineCode(item.machineCode)
    ? copy.machineCode[item.machineCode]
    : undefined
  const unknownMachineCode = machineCodeCopy
    ? undefined
    : safeUnknownMachineCode(item.machineCode)
  const localized =
    reportStatus === 'error'
      ? copy.checkFailed
      : machineCodeCopy ?? copy.generic[item.kind]
  return {
    ...item,
    ...localized,
    explanation:
      reportStatus !== 'error' && unknownMachineCode
        ? `${localized.explanation} ${copy.diagnosticCodeLabel}: ${unknownMachineCode}.`
        : localized.explanation,
    suggestion: localizedSuggestion(item, copy),
  }
}

export function localizeCompactMachineValidationReason(
  report: ReadinessReport,
  locale: LocaleCode,
): string | undefined {
  if (report.machineValidation.performed) return undefined
  if (locale === 'ko') return report.machineValidation.reason
  const copy = LOCALIZED_COPY[locale] ?? LOCALIZED_COPY.en
  return report.status === 'error'
    ? copy.machineValidationFailed
    : copy.machineValidationNotPerformed
}
